import { useIntersectionObserver } from "usehooks-ts";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";

import { useVideoQueueStore } from "@/common/store/videoQueueStore";

interface UsePlayerObserverProp {
  playerWrapRef: React.RefObject<HTMLDivElement>;
  position: number;
  fullscreen: boolean;
  gifId: string;
}

const FIRST_GIF_THRESHOLD = 0.9;
const FS_THRESHOLD = 0.5;

export const usePlayerObserver = ({
  playerWrapRef,
  position,
  fullscreen,
  gifId
}: UsePlayerObserverProp) => {
  const location = useLocation();
  const videoQueue = useVideoQueueStore((state) => state.videoQueue);
  const addToQueue = useVideoQueueStore((state) => state.addToQueue);
  const removeFromQueue = useVideoQueueStore((state) => state.removeFromQueue);
  const getLastVisibleVideo = useVideoQueueStore((state) => state.getLastVisibleVideo);
  const isFirstOnWatchPage = useMemo(() => location.pathname.includes("/watch") && position === 1, [location, position]);

  const entryActiveVideos = useIntersectionObserver(playerWrapRef, {});

  const entryVisibleVideo = useIntersectionObserver(playerWrapRef, {
    threshold: fullscreen ? FS_THRESHOLD : (isFirstOnWatchPage ? FIRST_GIF_THRESHOLD : 0),
    rootMargin: fullscreen ? "0px 0px 0px 0px" : (isFirstOnWatchPage ? "0px 0px 0px 0px" : "-50% 0px -50% 0px")
  });

  useEffect(() => {
    if (entryVisibleVideo?.isIntersecting) {
      addToQueue(gifId);
    } else if (videoQueue.includes(gifId)) {
      removeFromQueue(gifId);
    }
  }, [entryVisibleVideo, gifId]);

  const isVisible = useMemo(() => getLastVisibleVideo() === gifId, [videoQueue, gifId]);
  const isActive = !!entryActiveVideos?.isIntersecting;

  return {
    isVisible,
    isActive
  };
};
